import { memo } from "react";
import { Link } from "wouter";
import { ChevronRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface BackToContext {
  label: string;
  href: string;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
  backTo?: BackToContext;
  isLoading?: boolean;
}

function truncateLabel(label: string, maxLength: number = 24): string {
  if (label.length <= maxLength) return label;
  return label.substring(0, maxLength - 3) + "...";
}

function BreadcrumbComponent({ items, backTo, isLoading }: BreadcrumbProps) {

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 mb-4">
        <Skeleton className="h-5 w-24" />
        <ChevronRight className="h-4 w-4 text-gray-400" />
        <Skeleton className="h-5 w-32" />
      </div>
    );
  }

  const hasNavigation = (items && items.length > 0) || backTo;
  if (!hasNavigation) return null;

  return (
    <div className="flex items-center gap-3 mb-4 flex-wrap">
      {backTo && (
        <Link href={backTo.href}>
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 -ml-2 gap-1"
            data-testid="btn-back-to"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to {truncateLabel(backTo.label, 20)}</span>
            <span className="sm:hidden">Back</span>
          </Button>
        </Link>
      )}

      {backTo && items && items.length > 0 && (
        <div className="h-4 w-px bg-gray-300" />
      )}

      {items && items.length > 0 && (
        <nav aria-label="Breadcrumb" className="flex items-center">
          <ol className="flex items-center gap-1">
            {items.slice(0, 3).map((item, index) => {
              const isLast = index === items.length - 1 || index === 2;
              const displayLabel = truncateLabel(item.label);

              return (
                <li key={index} className="flex items-center">
                  {index > 0 && (
                    <ChevronRight className="h-4 w-4 text-gray-400 mx-1" />
                  )}
                  {item.href && !isLast ? (
                    <Link
                      href={item.href}
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                      data-testid={`breadcrumb-link-${index}`}
                    >
                      {displayLabel}
                    </Link>
                  ) : (
                    <span
                      className="text-sm text-gray-600 font-medium"
                      data-testid={`breadcrumb-current-${index}`}
                    >
                      {displayLabel}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}
    </div>
  );
}

export const Breadcrumb = memo(BreadcrumbComponent);
export default Breadcrumb;
